import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
// @material-ui/core
import {
  withStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  CircularProgress,
  Button,
  IconButton,
  Icon,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  TextField,
  Card,
  CardHeader,
  CardContent,
  Chip,
  Grid,
} from '@material-ui/core';
import {
  ToggleButtonGroup,
  ToggleButton,
} from '@material-ui/lab';
import { grey } from '@material-ui/core/colors';
// @material-ui/icons
import Close from '@material-ui/icons/Delete';

import {
  getTenantUsers,
  removeUserFromTenant,
} from '@pingum/app-state-management/tenant/actions';
import { showLogsModal } from '@pingum/app-state-management/log/actions';
import { addError } from '@pingum/app-state-management/error/actions';

import { formatDate } from '../../../../utils/dateHelper';

import UserRoles from './components/UserRoles';
import UserName from './components/UserName';
import AddUsers from './components/AddUsers';

import AuditLogsButton from '../../../widgets/AuditLogsButton';
import AboutUserRolesModal from '../../../modals/AboutUserRolesModal';

const styles = (theme) => ({
  root: {
    width: '100%',
    overflowX: 'auto',
  },
  table: { minWidth: 700 },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  button: { margin: theme.spacing(1) },
  userLimitDisplaimer: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: grey[100],
  },
  userChip: {
    fontSize: '.75rem',
    fontWeight: 500,
  },
  chipIcon: { fontSize: '1.5em' },
});

export const formatStringOfRolesIntoArray = (rolesString) => {
  if (rolesString === undefined) {
    return [];
  }
  let parsedRoleString = rolesString.replace('[', '');
  parsedRoleString = parsedRoleString.replace(']', '');
  parsedRoleString = parsedRoleString.replace(/\s/g, '');
  return parsedRoleString.split(',');
};

export class UsersCardComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      openConfirmRemoveUserDialog: false,
      selectedRemoveUser: {},
      search: '',
      page: 0,
      showAboutUserRolesModal: false,
      roleFilters: ['active', 'passive'],
      addingUsers: false,
    };

    this.props.getTenantUsers(this.props.tenantName);
  }

  handleRemoveClickOpen = (user) => {
    this.setState({
      openConfirmRemoveUserDialog: true,
      selectedRemoveUser: user,
    });
  }

  handleRemoveClickClose = () => {
    this.setState({
      openConfirmRemoveUserDialog: false,
      selectedRemoveUser: {},
    });
  }

  handleRemoveConfirm = () => {
    // can not remove yourself
    if (this.props.profile.user_id === this.state.selectedRemoveUser.auth0UserId) {
      this.props.addError('You cannot remove yourself. If you wish to leave the organization, do so from your organizations page.');
      this.setState({
        openConfirmRemoveUserDialog: false,
        selectedRemoveUser: {},
      });
      return;
    }

    this.setState({
      openConfirmRemoveUserDialog: false,
      selectedRemoveUser: {},
    });
    this.props.removeUserFromTenant(
      this.props.tenantName,
      this.state.selectedRemoveUser.auth0UserId,
    );
    this.setState({
      openConfirmRemoveUserDialog: false,
      selectedRemoveUser: {},
    });
  }

  showLogsModal = () => {
    this.props.showLogsModal({
      tenantName: this.props.tenantName,
      service: 'tenant',
      actions: ['User Left Organization', 'User(s) Added To Organization', 'User Removed From Organization', 'Organization User Updated'],
    });
  }

  handleSearchChange = (e) => {
    this.setState({ search: e.target.value });
  }

  handleChangePage = (event, page) => {
    this.setState({ page });
  }

  toggleShowAboutUserRolesModal = () => {
    this.setState((prevState) => ({ showAboutUserRolesModal: !prevState.showAboutUserRolesModal }));
  }

  handleRoleFilters = (e, roleFilters) => {
    this.setState({ roleFilters });
  }

  toggleAddingUsers = () => {
    this.setState((prevState) => ({ addingUsers: !prevState.addingUsers }));
  }

  render() {
    const onlyOneOwner = this.props.tenantUsers.filter((tenantUser) => tenantUser.roles.indexOf('owner') >= 0).length <= 1;
    const searchFilter = this.state.search.toLocaleLowerCase();
    let filteredAndSortedUsers = this.props.tenantUsers.sort((a, b) => b.email - a.email);
    filteredAndSortedUsers = filteredAndSortedUsers.filter((user) => ((this.state.roleFilters.indexOf('passive') >= 0 && user.passive)
      || (this.state.roleFilters.indexOf('active') >= 0 && !user.passive))
      && (user.email.toLocaleLowerCase().indexOf(searchFilter) >= 0
      || user.firstName.toLocaleLowerCase().indexOf(searchFilter) >= 0
      || user.lastName.toLocaleLowerCase().indexOf(searchFilter) >= 0
      || (`${user.firstName.toLocaleLowerCase()} ${user.lastName.toLocaleLowerCase()}`).indexOf(searchFilter) >= 0
      || user.roles.toLocaleLowerCase().indexOf(searchFilter) >= 0));

    const infoButton = (
      <Tooltip title="About User Roles">
        <IconButton onClick={this.toggleShowAboutUserRolesModal}>
          <Icon>
            info_outlined
          </Icon>
        </IconButton>
      </Tooltip>
    );

    return (
      <div>
        {
          this.state.addingUsers
          && (
            <AddUsers
              tenantName={this.props.tenantName}
              onDoneAddingUsers={this.toggleAddingUsers}
              infoButton={infoButton}
            />
          )
        }
        <Card>
          <CardHeader
            title="Organization Users"
            subheader="Manage existing or add new users to your organization"
            action={(
              <div>
                {
                  !this.state.addingUsers
                  && (
                    <Tooltip title="Add Users">
                      <IconButton onClick={this.toggleAddingUsers}>
                        <Icon>
                          add
                        </Icon>
                      </IconButton>
                    </Tooltip>
                  )

                }
                {infoButton}
                <AuditLogsButton onClick={this.showLogsModal} />
              </div>
            )}
          />
          <CardContent className={this.props.classes.root}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <ToggleButtonGroup
                  value={this.state.roleFilters}
                  onChange={this.handleRoleFilters}
                >
                  <ToggleButton value="active">
                    <Chip
                      icon={<Icon className={this.props.classes.chipIcon}>face</Icon>}
                      size="small"
                      label="Active"
                      className={this.props.classes.userChip}
                      color="secondary"
                      clickable
                    />
                  </ToggleButton>
                  <ToggleButton value="passive">
                    <Chip
                      icon={<Icon className={this.props.classes.chipIcon}>face</Icon>}
                      size="small"
                      label="Passive"
                      className={this.props.classes.userChip}
                      color="default"
                      clickable
                    />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12} sm={6} style={{ textAlign: 'right' }}>
                <TextField
                  style={{ margin: 0 }}
                  id="outlined-search-filter"
                  label="Search"
                  value={this.state.search}
                  onChange={this.handleSearchChange}
                  margin="normal"
                  variant="outlined"
                />
              </Grid>
            </Grid>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    style={{
                      maxWidth: 90,
                      minWidth: 90,
                    }}
                  >Avatar
                  </TableCell>
                  <TableCell align="left">Email</TableCell>
                  <TableCell align="left">Name</TableCell>
                  <TableCell align="left">Roles</TableCell>
                  <TableCell align="left" style={{ minWidth: 225 }}>Audit</TableCell>
                  <TableCell
                    align="center"
                    style={{
                      maxWidth: 90,
                      minWidth: 90,
                    }}
                  >Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                style={{
                  height: 100,
                  overflowY: 'auto',
                }}
              >
                {filteredAndSortedUsers
                  .slice(this.state.page * 10, (this.state.page * 10) + 10).map((user) => (
                    <TableRow key={user.id}>
                      <TableCell
                        align="center"
                        style={{
                          maxWidth: 90,
                          minWidth: 90,
                        }}
                      ><IconButton aria-haspopup="false"> <Avatar src={user.avatar} /> </IconButton>
                      </TableCell>
                      <TableCell align="left">{user.email}</TableCell>
                      <TableCell align="left"><UserName
                        tenant={this.props.tenantName}
                        tenantUser={user}
                        isLoading={this.props.isFetching}
                        onlyOneOwner={onlyOneOwner}
                      />
                      </TableCell>
                      <TableCell align="left">
                        <UserRoles
                          tenant={this.props.tenantName}
                          tenantUser={user}
                          onlyOneOwner={onlyOneOwner}
                          isLoading={this.props.isFetching}
                          infoButton={infoButton}
                        />
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 225 }}>
                        <div><small><b>Created By:</b> {user.addedByName}</small></div>
                        <div><small><b>Created Date:</b> {formatDate(user.addedDate)}</small></div>
                        <div>
                          <small>
                            <b>Last Updated By:</b>&nbsp;
                            {
                              !user.updatedByName
                                ? 'N/A'
                                : user.updatedByName
                            }
                          </small>
                        </div>
                        <div>
                          <small>
                            <b>Last Updated Date:</b>&nbsp;
                            {
                              !user.updatedDate
                                ? 'N/A'
                                : formatDate(user.updatedDate)
                            }
                          </small>
                        </div>
                        <div>
                          <small>
                            <b>Last Activity Date:</b>&nbsp;
                            {
                              !user.lastActivityDate
                                ? 'N/A'
                                : formatDate(user.lastActivityDate)
                            }
                          </small>
                        </div>
                      </TableCell>
                      <TableCell
                        align="center"
                        style={{
                          maxWidth: 90,
                          minWidth: 90,
                        }}
                      >
                        {
                          this.props.isFetching
                            ? (
                              <CircularProgress
                                size={20}
                                style={{
                                  marginLeft: 10,
                                  float: 'left',
                                }}
                                color="inherit"
                              />
                            )
                            : (
                              <Tooltip title="Remove User" id={'remove-user-tooltip'.concat(user.email)}>
                                <IconButton
                                  style={{ padding: 4 }}
                                  onClick={
                                  () => { this.handleRemoveClickOpen(user); }
                                }
                                  className="remove"
                                >
                                  <Close />
                                </IconButton>
                              </Tooltip>
                            )
                        }
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    colSpan={6}
                    count={filteredAndSortedUsers.length}
                    rowsPerPageOptions={[10]}
                    rowsPerPage={10}
                    page={this.state.page}
                    SelectProps={{ native: true }}
                    onChangePage={this.handleChangePage}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </CardContent>
        </Card>
        <Dialog
          open={this.state.openConfirmRemoveUserDialog}
          onClose={this.handleRemoveClickClose}
          aria-labelledby="remove_user-title"
          aria-describedby="remove-user-description"
          id="remove-user-dialog"
        >
          <DialogTitle id="remove-user-title">Confirm Action</DialogTitle>
          <DialogContent>
            <DialogContentText id="remove-user-description">
              Are you sure you want to remove the user
              <b>
                {' '.concat(this.state.selectedRemoveUser.email === undefined
                  ? ''
                  : this.state.selectedRemoveUser.email)}
              </b> from this organization? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => { this.handleRemoveClickClose(); }}
              id="cancel-remove-button"
            >
              Cancel
            </Button>
            <Button
              onClick={() => { this.handleRemoveConfirm(); }}
              color="primary"
              autoFocus
              id="confirm-remove-button"
            >
              Remove User
            </Button>
          </DialogActions>
        </Dialog>
        <AboutUserRolesModal
          open={this.state.showAboutUserRolesModal}
          onClose={this.toggleShowAboutUserRolesModal}
        />
      </div>
    );
  }
}

UsersCardComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  tenantName: PropTypes.string.isRequired,
  getTenantUsers: PropTypes.func.isRequired,
  removeUserFromTenant: PropTypes.func.isRequired,
  tenantUsers: PropTypes.array.isRequired,
  isFetching: PropTypes.bool.isRequired,
  showLogsModal: PropTypes.func.isRequired,
  addError: PropTypes.func.isRequired,
};

export const mapStateToProps = (state) => ({
  profile: state.user.user,
  tenantUsers: state.tenant.tenantUsers,
  isFetching: state.tenant.isFetchingTenantUsers,
});

export const mapDispatchToProps = (dispatch) => ({
  getTenantUsers: (tenantName) => dispatch(getTenantUsers(tenantName)),
  removeUserFromTenant: (tenantName, auth0UserId) => dispatch(removeUserFromTenant(tenantName, auth0UserId)),
  showLogsModal: (request) => dispatch(showLogsModal(request)),
  addError: (error) => dispatch(addError(error)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(UsersCardComponent));
